// Cliente HTTP tipado para la API de GetPay93 (Spring Boot, Cloud Run).
// Inyectable `fetch` para tests. Maneja Bearer token, JSON, y callbacks 401/402.

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export type QueryValue = string | number | boolean | undefined | null;

export interface RequestOptions {
  query?: Record<string, QueryValue>;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface HttpClient {
  request<T = unknown>(method: string, path: string, opts?: RequestOptions): Promise<T>;
  get<T = unknown>(path: string, opts?: RequestOptions): Promise<T>;
  post<T = unknown>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>;
  put<T = unknown>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>;
  patch<T = unknown>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>;
  del<T = unknown>(path: string, opts?: RequestOptions): Promise<T>;
}

export interface HttpClientOptions {
  baseUrl: string;
  getToken?: () => string | null | undefined;
  onUnauthorized?: () => void;
  onPaymentRequired?: (err: ApiError) => void;
  /** Inyectable para tests; por defecto usa el fetch global. */
  fetch?: typeof fetch;
}

function joinUrl(baseUrl: string, path: string, query?: Record<string, QueryValue>): string {
  const base = baseUrl.replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  let url = base + p;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) qs.append(k, String(v));
    }
    const s = qs.toString();
    if (s) url += `?${s}`;
  }
  return url;
}

function isPlainBody(body: unknown): boolean {
  // Estos tipos los envía fetch tal cual; no los serializamos ni forzamos content-type.
  return (
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    body instanceof URLSearchParams ||
    typeof body === 'string'
  );
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204 || res.status === 205) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}

export function createHttpClient(options: HttpClientOptions): HttpClient {
  const doFetch = options.fetch ?? globalThis.fetch;

  async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const url = joinUrl(options.baseUrl, path, opts.query);
    const headers = new Headers(opts.headers);
    if (!headers.has('accept')) headers.set('accept', 'application/json');

    const token = options.getToken?.();
    if (token) headers.set('authorization', `Bearer ${token}`);

    let body: BodyInit | undefined;
    if (opts.body !== undefined && opts.body !== null && method !== 'GET' && method !== 'HEAD') {
      if (isPlainBody(opts.body)) {
        body = opts.body as BodyInit;
      } else {
        if (!headers.has('content-type')) headers.set('content-type', 'application/json');
        body = JSON.stringify(opts.body);
      }
    }

    const res = await doFetch(url, { method, headers, body, signal: opts.signal });

    if (!res.ok) {
      const errBody = await parseBody(res);
      const fromBody =
        errBody && typeof errBody === 'object'
          ? 'error' in errBody && typeof (errBody as Record<string, unknown>).error === 'string'
            ? (errBody as Record<string, string>).error
            : 'message' in errBody && typeof (errBody as Record<string, unknown>).message === 'string'
              ? (errBody as Record<string, string>).message
              : undefined
          : undefined;
      const message = fromBody ?? `HTTP ${res.status} ${res.statusText}`.trim();
      const err = new ApiError(res.status, message, errBody);
      if (res.status === 401) options.onUnauthorized?.();
      if (res.status === 402) options.onPaymentRequired?.(err);
      throw err;
    }

    return (await parseBody(res)) as T;
  }

  return {
    request,
    get: (path, opts) => request('GET', path, opts),
    post: (path, body, opts) => request('POST', path, { ...opts, body }),
    put: (path, body, opts) => request('PUT', path, { ...opts, body }),
    patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
    del: (path, opts) => request('DELETE', path, opts),
  };
}
